import type { EChartsOption } from 'echarts'

import type { ChartTheme } from '../chartTheme'
import { axisMoneyFormatter, tooltipMoneyFormatter } from '../formatters'

export const buildTooltipBase = (theme: ChartTheme) => ({
  confine: true,
  trigger: 'axis' as const,
  backgroundColor: theme.tooltip.backgroundColor,
  borderWidth: 0,
  borderColor: 'transparent',
  extraCssText: `border-radius: 8px; box-shadow: 0 8px 24px ${theme.tooltip.shadowColor};`,
  textStyle: {
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSize,
    color: theme.tooltip.textColor,
    lineHeight: 20,
  },
  axisPointer: {
    lineStyle: {
      color: theme.axis.pointerColor,
      type: 'solid' as const,
      width: 1,
    },
  },
  valueFormatter: tooltipMoneyFormatter,
})

export const buildLegendBase = (theme: ChartTheme) => ({
  right: 0,
  padding: [0, 0, 0, 80] as [number, number, number, number],
  icon: 'circle',
  itemWidth: 8,
  itemHeight: 8,
  itemGap: 10,
  selectedMode: false as const,
  textStyle: {
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSize,
    color: theme.legend.textColor,
  },
})

export const buildTitle = (
  text: string,
  top: number | string,
  theme: ChartTheme
) => ({
  text,
  top,
  left: 0,
  padding: [0, 0, 0, 0] as [number, number, number, number],
  textStyle: {
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSize,
    fontWeight: 600,
    color: theme.titleColor,
  },
})

export const buildBaseOption = (
  theme: ChartTheme,
  title?: string
): EChartsOption => ({
  textStyle: {
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSize,
  },
  title: title ? buildTitle(title, 5, theme) : undefined,
  legend: {
    ...buildLegendBase(theme),
    top: 5,
  },
  grid: {
    top: theme.gridTop,
    left: 0,
    right: 16,
    bottom: 30,
    containLabel: true,
  },
  xAxis: {
    type: 'category',
    axisLine: {
      lineStyle: { color: theme.axis.lineColor },
    },
    axisTick: { show: false },
    axisLabel: {
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSize,
      color: theme.axis.labelColor,
      margin: 8,
    },
  },
  yAxis: {
    type: 'value',
    // Pin the bottom of the visible range to 0 — negative networth /
    // liquid-assets values still flow through the dataset (so the line
    // resumes correctly when it comes back up) but the negative portion
    // is clipped out of the rendered grid area.
    min: 0,
    splitLine: {
      lineStyle: { color: theme.axis.splitLineColor },
    },
    axisLine: { show: false },
    axisTick: { show: false },
    axisLabel: {
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSize,
      color: theme.axis.labelColor,
      formatter: axisMoneyFormatter,
    },
  },
  tooltip: buildTooltipBase(theme),
  dataZoom: [
    {
      type: 'inside',
    },
  ],
})
